<template>
  <section
    class="invoice-preview-wrapper"
    style="position: relative"
    aria-label="Détails de la feuille de route"
    role="main"
  >
    <!-- Alert: No item found -->
    <b-alert
      variant="danger"
      :show="roadmap === undefined"
    >
      <h4 class="alert-heading">
        Erreur lors de la récupération des données de la feuille de route
      </h4>
      <div class="alert-body">
        Aucune feuille de route trouvée avec cet identifiant. Vérifiez la liste des feuilles de route pour d'autres éléments.
      </div>
    </b-alert>
    <b-row
      v-if="roadmap"
      class="invoice-preview"
    >
      <!-- Col: Left (Invoice Container) -->
      <b-col
        cols="12"
        xl="9"
        md="8"
      >
        <b-card
          v-if="roadmap"
          no-body
          class="invoice-preview-card"
        >
          <!-- Header -->
          <div
            class="d-flex justify-content-center flex-md-row flex-column invoice-spacing mt-0"
          >
            <div style="color: black">
              <div>
                <img
                  src="@/assets/images/logo/logo.png"
                  width="250px"
                  height="100%"
                  alt="Logo de la société"
                >
              </div>
            </div>
          </div>

          <div class="d-flex flex-row justify-content-between align-items-start mb-4">
            <div class="ml-3">
              <h1
                id="roadmap-detail-title"
                style="color: black"
                aria-label="Titre de la feuille de route"
              >Feuille de route N° : {{ roadmap.id }}</h1>
            </div>
            <div class="mr-3">
              <h4 style="color: black; font-weight: bold; margin-top: 18px;">
                Date : {{ roadmap.date }}
              </h4>
            </div>
          </div>

          <!-- Two Cards Side by Side -->
          <b-card-body class="px-3">
            <div class="d-flex flex-row justify-content-between mb-4 cards-container">
              <!-- Company Card -->
              <div class="info-card company-card">
                <div
                  class="h3"
                  style="color:black"
                >
                  <strong>{{ company.name }}</strong> : {{ company.tagline || 'Société de livraison à domicile' }}
                </div>
                <div
                  class="h3"
                  style="color:black"
                >
                  <strong>Matricule fiscale :</strong> {{ company.tax_identification_number }}
                </div>
                <div
                  class="h3"
                  style="color:black"
                >
                  <strong>Adresse :</strong> {{ company.address }}
                </div>
                <div
                  class="h3"
                  style="color:black"
                >
                  <strong>Tel :</strong> {{ company.phone1 }}
                </div>
              </div>

              <!-- Delivery Man Card -->
              <div
                v-if="roadmap.delivery_man"
                class="info-card delivery-card"
              >
                <div
                  class="h3"
                  style="color:black"
                >
                  <strong>Livreur :</strong> {{ roadmap.delivery_man.username }}
                </div>
                <div
                  class="h3"
                  style="color:black"
                >
                  <strong>Tel livreur :</strong> {{ roadmap.delivery_man.phone }}
                </div>
                <div
                  v-if="roadmap.delivery_man.car"
                  class="h3"
                  style="color:black"
                >
                  <strong>Matricule voiture :</strong> {{ roadmap.delivery_man.car.serial_number }}
                </div>
                <div
                  class="h3"
                  style="color:black; font-size: 1.5rem; font-weight: bold;"
                >
                  <strong>Nombre des colis :</strong> {{ orders.length }}
                </div>
              </div>
            </div>
          </b-card-body>

          <!-- Invoice Description: Table -->
          <b-card-body class="invoice-padding pt-0">
            <b-table
              class="ta"
              responsive
              :items="orders"
              :fields="columns"
              aria-label="Tableau des colis de la feuille de route"
              caption-top
            >
              <template #cell(code)="data">
                <div class="code-cell">
                  <!-- <qrcode-vue
                    :value="data.item.code.toString()"
                    :size="size"
                    level="H"
                  /> -->
                  <span aria-label="Code du colis">{{ data.item.code }}</span>
                </div>
              </template>
              <template #cell(fournisseur)="data">
                <div class="fournisseur-cell">
                  <strong
                    style="font-size: 20px"
                    aria-label="Fournisseur"
                  >
                    {{ data.item.external_supplier_name !== null ? data.item.external_supplier_name : data.item.fournisseur }}
                  </strong>
                  <strong
                    v-if="data.item.external_supplier_phone === null"
                    style="font-size: 20px"
                    class="d-block"
                    aria-label="Téléphone fournisseur"
                  >{{ data.item.telFournisseur }} <br> {{ data.item.telFournisseur2 }}</strong>
                  <strong
                    v-else
                    style="font-size: 20px"
                    class="d-block"
                    aria-label="Téléphone fournisseur externe"
                  >{{ data.item.external_supplier_phone }}</strong>
                </div>
              </template>
              <template #cell(destinataire)="data">
                <div class="destinataire-cell">
                  <strong
                    style="font-size: 20px"
                    aria-label="Destinataire"
                  >{{ data.item.destinataire }}</strong>
                  <strong
                    style="font-size: 20px"
                    class="d-block"
                    aria-label="Téléphone destinataire"
                  >{{ data.item.tel }} <br>
                    {{ data.item.tel2 }}
                  </strong>
                </div>
              </template>
              <template #cell(gouvernorat)="data">
                <div class="gouvernorat-cell">
                  <strong
                    style="font-size: 20px"
                    aria-label="Adresse"
                  >
                    {{ data.item.gouvernorat }}<span v-if="data.item.delegation">, {{ data.item.delegation }}</span>
                  </strong>
                  <strong
                    style="font-size: 20px"
                    class="d-block"
                    aria-label="Adresse"
                  >{{ data.item.adresse }}</strong>
                </div>
              </template>
              <template #cell(info)="data">
                <div class="info-cell">
                  <span aria-label="Informations">{{ data.item.info }}</span>
                </div>
              </template>
              <template #cell(prix)="data">
                <div class="prix-cell">
                  <span aria-label="Prix">{{ data.item.prix }}</span>
                  <span
                    v-if="data.item.is_an_exchange"
                    style="color: #EA2F2F !important; font-weight: bold !important; font-size: 30px; display: block; margin-top: 5px; text-align: center;"
                  >
                    Echange
                  </span>
                  <span
                    v-if="data.item.can_open_package"
                    style="color: #C59C6C !important; font-weight: bold !important; font-size: 30px; display: block; margin-top: 5px; text-align: center;"
                  >
                    Ouvert
                  </span>
                </div>
              </template>
              <!-- <template #cell(last_return_reason)="data">
                <div class="return-reason-cell">
                  <span aria-label="Dernier motif de retour">
                    {{ (data.item.last_return_reason && typeof data.item.last_return_reason === 'object') ? data.item.last_return_reason.return_reason : (data.item.last_return_reason || '') }}
                  </span>
                </div>
              </template> -->
              <template #cell(action)="data">
                <div class="action-cell">
                  <span aria-label="Action">{{ data.item.action }}</span>
                </div>
              </template>
              <template #cell(return_number)="data">
                <div class="return-number-cell">
                  <span aria-label="Retour">Retour : {{ data.item.return_number }}</span>
                </div>
              </template>
            </b-table>
            <b-card-body class="d-flex justify-content-between mt-5">
              <!-- Col: Total -->
              <div class="mb-5 ml-1">
                <h3 style="color: black; font-size: 40px;font-weight: bold;">
                  Signature
                </h3>
              </div>
              <div>
                <h1 style="color: black; font-size: 40px;font-weight: bold;">
                  Total : {{ total.toFixed(3) }} DT
                </h1>
              </div>
            </b-card-body>
          </b-card-body>
        </b-card>
      </b-col>

      <!-- Right Col: Card -->
      <b-col
        cols="12"
        md="4"
        xl="3"
        class="invoice-actions"
      >
        <b-card>
          <!-- Button: Sort by Location -->
          <b-button
            v-ripple.400="'rgba(113, 102, 240, 0.15)'"
            variant="outline-primary"
            class="mb-75"
            block
            @click="sortByGovernorate"
          >
            Trier par localisation
          </b-button>
          <!-- Button: Print -->
          <b-button
            v-ripple.400="'rgba(186, 191, 199, 0.15)'"
            variant="outline-secondary"
            class="mb-75"
            block
            @click="printInvoice"
          >
            Imprimer
          </b-button>
        </b-card>
      </b-col>
    </b-row>
  </section>
</template>

<script>
import { ref } from '@vue/composition-api'
import {
  BRow,
  BCol,
  BCard,
  BCardBody,
  BTable,
  BButton,
  BAlert,
  VBToggle,
} from 'bootstrap-vue'

import Ripple from 'vue-ripple-directive'
import axios from 'axios'
import storeAuth from '@/store/store'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
// import QrcodeVue from 'qrcode.vue'

export default {
  directives: {
    Ripple,
    'b-toggle': VBToggle,
  },
  components: {
    BRow,
    BCol,
    BCard,
    BCardBody,
    BTable,
    BButton,
    BAlert,
    // QrcodeVue,
  },
  data() {
    return {
      p: [],
      roadmap: [],
      orders: [],
      company: {},
      size: 100,
      columns: [
        {
          key: 'code',
          label: 'BL',
          thClass: 'code-header',
          tdClass: 'code-cell',
        },
        {
          key: 'fournisseur',
          label: 'Fournisseur',
          thClass: 'fournisseur-header',
          tdClass: 'fournisseur-cell',
        },
        {
          key: 'destinataire',
          label: 'Destinataire',
          thClass: 'destinataire-header',
          tdClass: 'destinataire-cell',
        },
        {
          key: 'gouvernorat',
          label: 'Adresse',
          thClass: 'gouvernorat-header',
          tdClass: 'gouvernorat-cell',
        },
        {
          key: 'info',
          label: 'Info',
          thClass: 'info-header',
          tdClass: 'info-cell',
        },
        {
          key: 'return_number',
          label: 'NB Retour',
          thClass: 'return-number-header',
          tdClass: 'return-number-cell',
        },
        {
          key: 'prix',
          label: 'Prix',
          thClass: 'prix-header',
          tdClass: 'prix-cell',
        },
        // {
        //   key: 'last_return_reason',
        //   label: 'Motif retour',
        //   thClass: 'return-reason-header',
        //   tdClass: 'return-reason-cell',
        // },
      ],
    }
  },

  created() {
    this.company = storeAuth.state.company
    this.id = this.$route.params.id
    this.getRoadmap()
  },
  methods: {
    formatFn(date) {
      if (!date) {
        return null
      }
      const [year, month, day] = date.split('-')
      const day1 = day.substr(0, 2)
      const h = date.slice(11, 19)
      return `${year}-${month.padStart(2, '0')}-${day1.padStart(2, '0')} | ${h}`
    },
    formatDate(date) {
      if (!date) {
        return null
      }
      // Defensive: ensure date is a string and contains '-'
      if (typeof date !== 'string' || date.indexOf('-') === -1) {
        return date
      }
      const parts = date.split('-')
      if (parts.length < 3) {
        return date
      }
      const [year, month, day] = parts
      const day1 = day.substr(0, 2)
      return `${day1.padStart(2, '0')}/${month.padStart(2, '0')}/${year}`
    },

    async getRoadmap() {
      const { data } = await axios.get(`/api/roadmaps/detail/${this.$route.params.id}`)
      this.roadmap = data

      const { orders } = this.roadmap
      const mappedOrders = []
      for (let i = 0; i < orders.length; i += 1) {
        const order = orders[i]
        let dataObj
        if (order.delegation != null) {
          dataObj = {
            code: order.id,
            gouvernorat: order.governorate,
            delegation: order.delegation,
            locality: order.locality,
            fournisseur: order.supplier.username,
            telFournisseur: order.supplier.phone,
            telFournisseur2: order.supplier.phone2,
            tel: order.phone1,
            tel2: order.phone2,
            destinataire: order.name,
            adresse: order.address,
            prix: order.price_ttc,
            date: this.formatDate(order.created_at),
            return_number: order.return_number, // <-- ensure correct mapping
            is_discharge: order.is_discharge,
            is_fragile: order.is_fragile,
            is_an_exchange: order.is_an_exchange,
            can_open_package: order.can_open_package,
            external_supplier_name: order.external_supplier_name,
            external_supplier_phone: order.external_supplier_phone,
            last_return_reason: order.last_return_reason,
          }
        } else {
          dataObj = {
            code: order.id,
            gouvernorat: order.governorate,
            delegation: null,
            locality: null,
            fournisseur: order.supplier.username,
            telFournisseur: order.supplier.phone,
            telFournisseur2: order.supplier.phone2,
            tel: order.phone1,
            tel2: order.phone2,
            destinataire: order.name,
            adresse: order.address,
            prix: order.price_ttc,
            date: this.formatDate(order.created_at),
            return_number: order.return_number, // <-- ensure correct mapping
            is_discharge: order.is_discharge,
            is_fragile: order.is_fragile,
            is_an_exchange: order.is_an_exchange,
            can_open_package: order.can_open_package,
            external_supplier_name: order.external_supplier_name,
            external_supplier_phone: order.external_supplier_phone,
            last_return_reason: order.last_return_reason,
          }
        }
        mappedOrders.push(dataObj)
      }
      this.orders = mappedOrders // <-- assign array at once for reactivity
      this.total = this.roadmap.orders.reduce((accumulator, item) => accumulator + Number(item.price_ttc), 0)
    },
    async sortByGovernorate() {
      try {
        // Sort orders by governorate, then delegation, then locality
        const sortedOrders = [...this.orders].sort((a, b) => {
          // Compare governorates first
          const govA = a.gouvernorat || ''
          const govB = b.gouvernorat || ''
          const govCompare = govA.localeCompare(govB, 'fr', { sensitivity: 'base' })

          if (govCompare !== 0) {
            return govCompare
          }

          // If governorates are the same, compare delegations
          const delA = a.delegation || ''
          const delB = b.delegation || ''
          const delCompare = delA.localeCompare(delB, 'fr', { sensitivity: 'base' })

          if (delCompare !== 0) {
            return delCompare
          }

          // If delegations are the same, compare localities (if available)
          // Note: locality might not be in the mapped data, so we handle it gracefully
          const locA = a.locality || ''
          const locB = b.locality || ''
          return locA.localeCompare(locB, 'fr', { sensitivity: 'base' })
        })

        // Update the orders display
        this.orders = sortedOrders

        // Extract order IDs in the new sorted order
        const orderedIds = sortedOrders.map(order => order.code)

        // Send the new order to the backend
        await axios.post(`/api/roadmaps/sort-orders/${this.$route.params.id}/`, {
          order_ids: orderedIds,
        })

        // Show success message
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Succès',
            icon: 'CheckIcon',
            text: 'Les colis ont été triés par localisation (gouvernorat, délégation, localité)',
            variant: 'success',
          },
        })
      } catch (error) {
        console.error('Error sorting orders:', error)
        const errorMessage = error.response?.data?.error || 'Erreur lors du tri des colis'
        const debugInfo = error.response?.data?.debug

        if (debugInfo) {
          console.log('Debug info:', debugInfo)
        }

        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Erreur',
            icon: 'AlertTriangleIcon',
            text: errorMessage,
            variant: 'danger',
          },
        })
      }
    },
  },
  setup() {
    const invoiceData = ref(null)
    const paymentDetails = ref({})
    const sommeFrais = 0
    const total = 0
    const tax = 0
    const sommeHT = 0
    const printInvoice = () => {
      window.print()
    }

    return {
      invoiceData,
      paymentDetails,
      printInvoice,
      sommeHT,
      tax,
      total,
      sommeFrais,
    }
  },
}
</script>

<style lang="scss" scoped>
@import "~@core/scss/base/pages/app-invoice.scss";

// Cards styling
.cards-container {
  gap: 20px;

  .info-card {
    border: 2px solid black;
    padding: 20px;
    background: white;
    flex: 1;

    .h3 {
      margin-bottom: 10px;
      font-size: 1.7rem; // Increased font size
      font-weight: bold;
      &:last-child {
        margin-bottom: 0;
      }
    }
  }

  .company-card {
    margin-right: 10px;
  }

  .delivery-card {
    margin-left: 10px;
  }
}

table,
td,
th {
  text-align: left;
  border: 1px solid black;
}

footer {
  bottom: 0;
  height: auto;
}

table {
  width: 100%;
  border-collapse: collapse;
}

.mycolor {
  background-color: #EA2F2F;
}

.contact {
  background-color: #EA2F2F;
  padding: 10px;
  border-radius: 20px;
}

// Accessibilité : focus visible pour les champs interactifs
input:focus, select:focus, .v-select:focus {
  outline: 2px solid #005fcc !important;
  outline-offset: 2px;
}

.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0,0,0,0);
  border: 0;
}
</style>

<style lang="scss">
// Enhanced print styles with content-based sizing
@media print {
  html,
  body {
    margin: 0 !important;
    padding: 0 !important;
    height: auto !important;
    min-height: 0 !important;
  }

  .app-content,
  .content,
  .content-wrapper,
  .content-body {
    min-height: 0 !important;
    height: auto !important;
  }

  @page {
    size: A2 landscape;
    margin: 0;
  }

  // Hide non-essential elements
  .invoice-actions {
    display: none !important;
  }

  nav.header-navbar,
  .main-menu,
  .header-navbar-shadow,
  footer.footer,
  .customizer-toggle {
    display: none !important;
  }

  // Main content adjustments
  .content.app-content {
    margin-left: 0 !important;
    padding: 0 !important;
  }

  // Table with flexible, content-based sizing
  .ta {
    table-layout: auto !important;
    width: auto !important; // Let table size itself based on content
    border-collapse: collapse !important;
    font-size: 16px !important;
    color: black !important;

    // Header styles
    thead th {
      font-size: 16px !important;
      font-weight: bold !important;
      padding: 8px 8px !important;
      text-align: center !important;
      vertical-align: middle !important;
      word-wrap: break-word !important;
      white-space: nowrap !important;
      line-height: 1.2 !important;
      border: 1px solid black !important;
      margin: 0 !important;
    }

    // Body cell styles
    tbody td {
      font-size: 16px !important;
      padding: 4px 6px !important;
      vertical-align: top !important;
      word-wrap: break-word !important;
      line-height: 1.2 !important;
      border: 1px solid black !important;
      margin: 0 !important;
    }

    // Content-based cell sizing - Remove all min/max width constraints
    .code-cell, .fournisseur-cell, .code-header {
      text-align: center !important;
      min-height: 80px !important;
    }

    .fournisseur-cell, .fournisseur-header {
      width: auto !important;
      min-height: 50px !important;
    }

    .info-cell, .info-header {
      width: auto !important;
    }

    .prix-cell, .prix-header {
      width: 1% !important;
      max-width: 100px !important;
      white-space: nowrap !important;
      text-align: center !important;
    }

    .return-reason-cell, .return-reason-header {
      width: auto !important;
    }

    .gouvernorat-cell, .gouvernorat-header {
      width: auto !important;
    }

    .destinataire-cell, .destinataire-header {
      width: auto !important;
    }

    // Cell content styling
    .code-cell {
      display: flex !important;
      flex-direction: column !important;
      align-items: center !important;
      justify-content: center !important;
      gap: 6px !important;
      padding: 6px 0 !important;
      min-height: 100px !important;

      canvas {
        display: block !important;
        margin: 0 auto !important;
        width: 30px !important;
        height: 30px !important;
      }

      span {
        font-size: 20 !important;
        font-weight: bold !important;
        margin: 0 !important;
        text-align: center !important;
      }

      h4 {
        font-size: 20px !important;
        margin: 0 !important;
        color: red !important;
        font-weight: bold !important;
      }
    }

    .fournisseur-cell, .destinataire-cell {
      strong {
        font-size: 20px !important;
        line-height: 1.5 !important;
        display: block !important;
        margin-bottom: 2px !important;
        word-break: break-word !important;
        text-align: center !important;

        &:last-child {
          margin-bottom: 0 !important;
        }
      }
    }

    .gouvernorat-cell {
      strong {
        font-size: 11px !important;
        line-height: 1.1 !important;
        display: block !important;
        margin-bottom: 2px !important;
        word-break: break-word !important;
        hyphens: auto !important;
        text-align: center !important;

        &:last-child {
          margin-bottom: 0 !important;
        }
      }
    }

    .info-cell, .prix-cell, .return-reason-cell {
      span {
        font-size: 11px !important;
        word-break: break-word !important;
        // Make price bold
        font-weight: bold !important;
      }
    }
  }

  // Cards container adjustments
  .cards-container {
    display: flex !important;
    flex-direction: row !important;
    justify-content: space-between !important;
    gap: 15px !important;
    margin-bottom: 15px !important;

    .info-card {
      border: 2px solid black !important;
      color: black !important;
      background: white !important;
      padding: 10px !important;
      flex: 1 !important;

      .h3 {
        color: black !important;
        margin-bottom: 6px !important;
        font-size: 0.85rem !important;
        line-height: 1.2 !important;

        &:last-child {
          margin-bottom: 0 !important;
        }
      }
    }
  }

  // Header section adjustments
  .invoice-preview-card {
    .d-flex.flex-row.justify-content-between.align-items-start {
      margin-bottom: 15px !important;

      h1 {
        font-size: 1.5rem !important;
        margin: 0 !important;
      }

      h4 {
        font-size: 1.5rem !important;
        margin: 0 !important;
      }
    }
  }

  // Logo adjustments
  img {
    max-width: 150px !important;
    height: auto !important;
  }

  // Total and signature section
  .d-flex.justify-content-between.mt-5 {
    margin-top: 10px !important;
    h3, h1 {
      font-size: 2rem !important;
    }
  }

  // Force all text to be black
  * {
    color: black !important;
  }

  // Remove box shadows and backgrounds
  .card {
    background-color: transparent !important;
    box-shadow: none !important;
  }

  // Ensure proper page breaks
  .invoice-preview-wrapper {
    page-break-inside: auto !important;
    page-break-before: auto !important;
    margin: 0 !important;
    padding: 0 !important;
    min-height: 0 !important;
    height: auto !important;
  }

  // Remove backgrounds
  body {
    background-color: transparent !important;
  }

  // Color preservation for important elements
  .mycolor {
    background-color: #EA2F2F !important;
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }

  .contact {
    background-color: #EA2F2F !important;
    padding: 10px !important;
    border-radius: 20px !important;
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }

  .ta {
    .gouvernorat-cell strong,
    .info-cell span,
    .prix-cell span,
    .return-number-cell span {
      font-size: 20px !important;
      font-weight: bold !important;
    }
  }
}

// Screen styles for better development view
@media screen {
  .ta {
    .code-cell, .fournisseur-cell {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      gap: 6px;
      padding: 6px 0;
      min-height: 50px;
    }
    .fournisseur-cell {
      min-height: 50px;
    }

    .prix-cell {
      text-align: right;
    }

    .fournisseur-cell, .destinataire-cell, .gouvernorat-cell {
      strong {
        display: block;
        margin-bottom: 4px;
      }
    }
  }
}
</style>
